import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Box, InputAdornment } from "@mui/material";

import { Layout } from "Layout";

import { NEW_RECORD_SKELETON } from "appConfig";

import { EditModalHeader } from "components/EditModalHeader";
import { FrmFieldGrid } from "components/FrmFieldGrid";
import { ObjectEditDialogContent } from "components/ObjectEditDialogContent";
import { FrmResizeTextField } from "components/formFields/FrmResizeTextField";
import { FrmSelectScalarWithObjectOptionsWithHoverHelp } from "components/formFields/FrmSelectScalarWithObjectOptions";
import { FrmTextField } from "components/formFields/FrmTextField";
import { FrmTextFieldWithHoverHelp } from "components/formFields/FrmTextFieldWithHoverHelp";
import { FrmCheckboxWithHoverHelp } from "components/formFields/frm/FrmCheckboxWithHoverHelp";
import { FixedLoadingIndicator } from "components/utility/FixedLoadingIndicator";
import { FormErrors } from "components/utility/FormErrors";
import { Loading } from "components/utility/Loading";
import { VSpace } from "components/utility/VSpace";

import { i18n } from "services/i18nService";
import {
  CONNECTION_TYPE_OPTIONS,
  OUTBOUND_CONNECTION_TYPE_OPTION,
  INBOUND_CONNECTION_TYPE_OPTION,
} from "services/sosInventoryService/ftpConnection/schema";
import { getRecord } from "services/sosInventoryService/sosApi";

import { useErrors } from "hooks/useErrors";
import { useRecord } from "hooks/useRecord";

import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

export function FtpConnection(props) {
  const { objectType, id, onClose } = props;

  const dispatch = useDispatch();

  const [record, setRecord, handleInputFieldChange] = useRecord();
  const { errors, setErrors, isInError } = useErrors();

  const loadingState = useSelector((state) => state.loading.editModal);

  useEffect(() => {
    async function _getRecord() {
      if (id) {
        dispatch(editModalLoadingIndicatorOn());
        const response = await getRecord(objectType, id);
        setRecord(response);
        dispatch(editModalLoadingIndicatorOff());
      } else {
        const emptyRecord = NEW_RECORD_SKELETON[objectType]();
        setRecord(emptyRecord);
      }
    }
    _getRecord();
  }, [id, dispatch, setRecord, objectType]);

  return (
    <>
      <EditModalHeader
        record={record}
        setRecord={setRecord}
        onSaveAndKeepEditing={setRecord}
        objectType={objectType}
        setErrors={setErrors}
        text={record?.name || ""}
        handleClose={onClose}
      />

      {loadingState && <Loading />}

      {record && (
        <ObjectEditDialogContent>
          <Layout
            pageTitle={
              id
                ? `${i18n(`objectType.${objectType}.Sentence`)} ${
                    record?.name || ""
                  }`
                : i18n(`objectType.${objectType}.New`)
            }
          >
            <Box flexGrow="1" position="relative" m={2} mb={0}>
              <FormErrors errors={errors} setErrors={setErrors} />
              {record ? (
                <Box>
                  <FrmFieldGrid gridAutoFlow="dense">
                    <div
                      style={{ gridColumn: "1 / 2", maxWidth: "24rem" }}
                    ></div>
                  </FrmFieldGrid>
                  <FrmFieldGrid gridAutoFlow="dense">
                    <div style={{ gridColumn: "1 / 2", maxWidth: "24rem" }}>
                      <FrmTextField
                        name="name"
                        value={record.name}
                        label={i18n("frmLabel.Name")}
                        onValueBlur={handleInputFieldChange}
                        error={isInError("name")}
                      />
                    </div>
                    <div style={{ gridColumn: "1 / 2", maxWidth: "24rem" }}>
                      <FrmResizeTextField
                        name="description"
                        value={record.description}
                        label={i18n("frmLabel.Description")}
                        minRows={2}
                        width="100%"
                        onValueBlur={handleInputFieldChange}
                      />
                      <FrmSelectScalarWithObjectOptionsWithHoverHelp
                        selectProps={{
                          name: "connectionType",
                          label: "Connection type",
                          value: record.connectionType,
                          onValueChange: handleInputFieldChange,
                          options: CONNECTION_TYPE_OPTIONS,
                          minWidth: "24rem",
                          disableClearable: true,
                        }}
                        helpText={i18n("ftp.connectionType.hoverHelp")}
                      />
                      <VSpace space={1} />
                      <FrmCheckboxWithHoverHelp
                        name="enabled"
                        value={record.enabled}
                        onValueChange={handleInputFieldChange}
                        label={i18n("frmLabel.Enabled")}
                        helpText={i18n("ftp.enabled.hoverHelp")}
                      />
                      <VSpace space={1} />
                      <FrmTextFieldWithHoverHelp
                        name="remoteHost"
                        value={record.remoteHost}
                        label={i18n("frmLabel.Host")}
                        onValueBlur={handleInputFieldChange}
                        helpText={i18n("ftp.remoteHost.hoverHelp")}
                        error={isInError("remoteHost")}
                        startAdornment={
                          <InputAdornment
                            position="start"
                            disableTypography
                            sx={{
                              lineHeight: "normal",
                              color: "disabled",
                              marginRight: 0,
                            }}
                          >
                            ftp://
                          </InputAdornment>
                        }
                      />
                      <FrmTextFieldWithHoverHelp
                        name="username"
                        value={record.username}
                        label={i18n("frmLabel.FtpUsername")}
                        onValueBlur={handleInputFieldChange}
                        helpText={i18n("ftp.credential.hoverHelp")}
                      />

                      <FrmTextFieldWithHoverHelp
                        name="password"
                        value={record.password}
                        label={i18n("frmLabel.FtpPassword")}
                        onValueBlur={handleInputFieldChange}
                        helpText={i18n("ftp.credential.hoverHelp")}
                      />
                      {record.connectionType !==
                        INBOUND_CONNECTION_TYPE_OPTION.id && (
                        <FrmTextFieldWithHoverHelp
                          name="outboundRemotePath"
                          value={record.outboundRemotePath}
                          label={i18n("frmLabel.OutboundRemotePath")}
                          onValueBlur={handleInputFieldChange}
                          helpText={i18n("ftp.path.hoverHelp")}
                        />
                      )}
                      {record.connectionType !==
                        OUTBOUND_CONNECTION_TYPE_OPTION.id && (
                        <FrmTextFieldWithHoverHelp
                          name="inboundRemotePath"
                          value={record.inboundRemotePath}
                          label={i18n("frmLabel.InboundRemotePath")}
                          onValueBlur={handleInputFieldChange}
                          helpText={i18n("ftp.path.hoverHelp")}
                        />
                      )}
                    </div>
                  </FrmFieldGrid>
                  <VSpace space={1} />
                  <FrmFieldGrid gridAutoFlow="dense"></FrmFieldGrid>
                </Box>
              ) : (
                <FixedLoadingIndicator text={`${i18n("global.Loading")}...`} />
              )}
            </Box>
            <VSpace space={4} />
          </Layout>
        </ObjectEditDialogContent>
      )}
    </>
  );
}
